import { Button } from '@app/components/various/Button';
import { Checkbox } from '@app/components/various/Fields/Checkbox';
import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, Form, FormFieldset, FormGrid, InputField } from '@app/components/various/Form';
import { StickyModalFooter } from '@app/components/various/Modal';
import { getCountries, getMarkets } from '@app/ducks';
import { useCreateAccount } from '@app/services/hooks/account/useCreateAccount';
import { usePaymentTermsSelect } from '@app/services/hooks/account/usePaymentTermsSelect';
import { useSalesRepSelect } from '@app/services/hooks/account/useSalesRepSelect';
import { useShippingTermsSelect } from '@app/services/hooks/account/useShippingTermsSelect';
import { useCreateNewSelectionFromWishlist } from '@app/services/hooks/lookbook/useCreateNewSelectionFromWishlist';
import { usePricelists } from '@app/services/hooks/pricelists/usePricelists';
import { useCreateAccountFromRequest } from '@app/services/hooks/requests/useCreateAccountFromRequest';
import { useAmplitude } from '@app/utils/analytics';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getStateSchema } from '@app/utils/schemas';
import { AccountFormSettings, CreateAccountSuccessResponse } from '@typings';
import { AxiosError } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useCreateAccountModalContext } from './CreateAccountModalContext';

export interface CreateAccountFormValue {
  email: string;
  firstName: string;
  lastName: string;
  market: Nullable<string>;
  accountName: string;
  pricelist: Nullable<string>;
  country: Nullable<string>;
  state: Nullable<string>;
  address: Nullable<string>;
  address2: Nullable<string>;
  city: Nullable<string>;
  zipCode: Nullable<string>;
  shippingTerm: Nullable<string>;
  paymentTerm: Nullable<string>;
  salesRep: Nullable<string>;
}

interface Props {
  onCancel: () => void;
  onSuccess?: () => void;
  formSettings: AccountFormSettings;
}

export const CreateAccountFormContent = ({ onCancel, onSuccess, formSettings }: Props) => {
  const { t } = useTranslation(['accounts', 'buyers', 'common', 'shipping', 'validation']);
  const countries = useSelector(getCountries);
  const markets = useSelector(getMarkets);
  const { products, deliveryWindows, shouldCreateSelection, hasWishlistedProducts, accountRequestId, formDefaultValues, createSource } =
    useCreateAccountModalContext();
  const { data: pricelists = [], isPending: isLoadingPricelists } = usePricelists();

  const defaultValues: CreateAccountFormValue = {
    accountName: '',
    address: '',
    address2: '',
    city: '',
    country: null,
    email: '',
    firstName: '',
    lastName: '',
    market: null,
    paymentTerm: null,
    pricelist: null,
    salesRep: formSettings.defaultSalesRep?.id.toString() ?? null,
    shippingTerm: null,
    state: null,
    zipCode: '',
    ...formDefaultValues,
  };

  const {
    handleLoadMore: loadMoreSalesReps,
    handleSearchChange: salesRapsSearchChange,
    isLoading: isLoadingSalesRep,
    options: salesRepOptions,
  } = useSalesRepSelect(formSettings.defaultSalesRep);
  const {
    handleLoadMore: loadMoreShippingTerms,
    handleSearchChange: shippingTermsSearchChange,
    isLoading: isLoadingShippingTerms,
    options: shippingTermOptions,
  } = useShippingTermsSelect();
  const {
    handleLoadMore: loadMorePaymentTerms,
    handleSearchChange: paymentTermsSearchChange,
    isLoading: isLoadingPaymentTerms,
    options: paymentTermOptions,
  } = usePaymentTermsSelect();

  const { mutate, isPending: isCreateAccountPending } = useCreateAccount();
  const { mutate: createAccountFromRequest, isPending: isLoadingCreateAccountFromRequest } = useCreateAccountFromRequest();
  const { mutate: createNewSelection, isPending: isCreateSelectionPending } = useCreateNewSelectionFromWishlist({});

  const isPending = isCreateAccountPending || isCreateSelectionPending || isLoadingCreateAccountFromRequest;
  const [includeWishlistedProducts, setIncludeWishlistedProducts] = React.useState(false);

  const validationSchema: yup.ObjectSchema<CreateAccountFormValue> = yup.object({
    accountName: yup.string().required(t('validation:account_name_hint')),
    address: yup.string().defined().nullable(),
    address2: yup.string().defined().nullable(),
    city: yup.string().defined().nullable(),
    country: yup.string().required(t('validation:country_hint')),
    email: yup.string().email(t('validation:invalid_email_hint')).required(t('validation:buyers_email_address_hint')),
    firstName: yup.string().required(t('validation:first_name_hint')),
    lastName: yup.string().required(t('validation:last_name_hint')),
    market: yup.string().required(t('validation:select_account_market_hint')),
    paymentTerm: yup.string().defined().nullable(),
    pricelist: yup.string().required(t('validation:select_account_pricelist_hint')),
    salesRep: yup.string().defined().nullable(),
    shippingTerm: yup.string().defined().nullable(),
    state: getStateSchema(countries),
    zipCode: yup.string().defined().nullable(),
  });

  const formMethods = useValidatedForm<CreateAccountFormValue>({ defaultValues, validationSchema });
  const { register, setValue, setError, watch } = formMethods;
  const selectedCountry = watch('country');

  const handleCountryChange = () => {
    setValue('state', null);
  };
  const { trackEvent } = useAmplitude();

  const countryOptions = React.useMemo(() => {
    return Object.values(countries).map(({ country, name }) => ({ country, name }));
  }, [countries]);

  const stateOptions = React.useMemo(() => {
    if (!isDefined(selectedCountry)) {
      return [];
    }

    return Object.values(countries[selectedCountry]?.states ?? {});
  }, [countries, selectedCountry]);
  const handleSuccess = (response: CreateAccountSuccessResponse) => {
    if (shouldCreateSelection && includeWishlistedProducts) {
      createNewSelection(
        {
          data: {
            buyers: [{ buyer: response.buyer.cus_id }],
            deliveryWindows: [...(isDefined(deliveryWindows) ? deliveryWindows : [])],
            products: [...(isDefined(products) ? products : [])],
          },
        },
        {
          onSuccess: () => {
            trackEvent({ name: 'account.createdWithSelection' });
          },
        },
      );

      return;
    }

    trackEvent({ name: 'account.created' });
    onSuccess?.();
  };

  const handleError = (error: AxiosError<Responses.Errors>) => {
    const errors = error.response?.data.errors;

    if (typeof errors === 'object') {
      Object.entries(errors).forEach(([field, message]) => {
        setError(field as keyof CreateAccountFormValue, { message });
      });
    }
  };

  const handleSubmit = (data: CreateAccountFormValue) => {
    if (isDefined(accountRequestId)) {
      createAccountFromRequest(
        {
          data: { ...data, createSource: createSource === 'lookbook' ? 'lookbook' : undefined },
          id: accountRequestId,
        },
        {
          onError: handleError,
          onSuccess: handleSuccess,
        },
      );
    } else {
      mutate(
        { ...data, createSource: createSource === 'lookbook' ? 'lookbook' : undefined },
        {
          onError: handleError,
          onSuccess: handleSuccess,
        },
      );
    }
  };

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit}>
      <FormFieldset isDisabled={isPending}>
        <h3 className="mb-3 heading-4">{t('accounts:account_one')}</h3>
        <FormGrid cols={2} colGap="medium">
          <InputField label={t('common:name')} placeholder={t('accounts:account_name')} isRequired {...register('accountName')} />
          <AutocompleteField name="market" placeholder={t('common:select_market')} label={t('common:market')} isRequired>
            {markets.map(({ id, name }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </AutocompleteField>
          <AutocompleteField
            name="pricelist"
            label={t('common:pricelist')}
            placeholder={t('common:select_pricelist')}
            isLoadingOptions={isLoadingPricelists}
            isRequired
          >
            {pricelists.map(({ id, name }) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </AutocompleteField>
          <AutocompleteField
            label={t('accounts:sales_rep')}
            isLoadingOptions={isLoadingSalesRep}
            overflowContainer
            name="salesRep"
            allowClear
            placeholder={t('accounts:sales_rep_select')}
            onSearchChange={salesRapsSearchChange}
            onLoadMore={loadMoreSalesReps}
          >
            {salesRepOptions.map(option => (
              <Option maskData key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </AutocompleteField>
        </FormGrid>
      </FormFieldset>
      <FormFieldset isDisabled={isPending}>
        <h3 className="mb-3 mt-4 heading-4">{t('buyers:buyer_one')}</h3>
        <FormGrid cols={2}>
          <InputField label={t('common:first_name')} isRequired {...register('firstName')} />
          <InputField label={t('common:last_name')} isRequired {...register('lastName')} />
          <InputField label={t('common:email')} isRequired {...register('email')} />
        </FormGrid>
      </FormFieldset>
      <FormFieldset isDisabled={isPending}>
        <h3 className="mb-3 mt-4 heading-4">{t('shipping:shipping')}</h3>
        <FormGrid cols={2} className="mb-6">
          <InputField label={t('common:address')} placeholder={t('common:address')} {...register('address')} isRequired={false} />
          <InputField
            label={t('common:address_continued')}
            placeholder={t('common:address_continued')}
            {...register('address2')}
            isRequired={false}
          />
          <InputField label={t('common:city')} placeholder={t('common:city')} {...register('city')} isRequired={false} />
          <InputField label={t('common:zip_code')} placeholder={t('common:zip_code')} {...register('zipCode')} isRequired={false} />
        </FormGrid>
        <FormGrid cols={2}>
          <AutocompleteField name="country" label={t('common:country')} isRequired onChange={handleCountryChange}>
            {countryOptions.map(({ country, name }) => (
              <Option key={country} value={country}>
                {name}
              </Option>
            ))}
          </AutocompleteField>
          {!isEmpty(stateOptions) && (
            <AutocompleteField name="state" label={t('common:state')} isRequired>
              {stateOptions.map(({ state, name }) => (
                <Option key={state} value={state}>
                  {name}
                </Option>
              ))}
            </AutocompleteField>
          )}
        </FormGrid>
      </FormFieldset>
      <FormFieldset isDisabled={isPending}>
        <FormGrid cols={2}>
          <AutocompleteField
            label={t('accounts:shipping_term')}
            isLoadingOptions={isLoadingShippingTerms}
            overflowContainer
            allowClear
            name="shippingTerm"
            placeholder={t('accounts:shipping_term_select')}
            onSearchChange={shippingTermsSearchChange}
            onLoadMore={loadMoreShippingTerms}
          >
            {shippingTermOptions.map(option => (
              <Option maskData key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </AutocompleteField>
          <AutocompleteField
            label={t('accounts:payment_term')}
            isLoadingOptions={isLoadingPaymentTerms}
            overflowContainer
            name="paymentTerm"
            allowClear
            placeholder={t('accounts:payment_term_select')}
            onSearchChange={paymentTermsSearchChange}
            onLoadMore={loadMorePaymentTerms}
          >
            {paymentTermOptions.map(option => (
              <Option maskData key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </AutocompleteField>
          {shouldCreateSelection && (
            <Checkbox
              isChecked={includeWishlistedProducts}
              onChange={() => setIncludeWishlistedProducts(prev => !prev)}
              isDisabled={!hasWishlistedProducts}
            >
              <span>{t('accounts:include_wishlisted_products')}</span>
            </Checkbox>
          )}
        </FormGrid>
      </FormFieldset>
      <StickyModalFooter>
        <div className="flex justify-end gap-2">
          <Button color="secondary" variant="ghost" disabled={isPending} onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button type="submit" isLoading={isPending}>
            {t('common:create')}
          </Button>
        </div>
      </StickyModalFooter>
    </Form>
  );
};
